import { Link, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod/v4";
import type { HTTPError } from "ky";
import { PolygrafLogo } from "@/components/icons/logo";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/passwordInput";
import { SubmitterText } from "@/components/ui/submit-button";
import { registerCompany } from "@/lib/http/registration";
import { TERMS_VERSION } from "@/lib/registration-data";
import { parseBackendError, parseRefusal } from "@/lib/utils/backend";
import { newPassword, requiredEmail } from "@/lib/utils/zod";
import { AuthMarketingHeroCopy } from "./-components/auth-marketing-hero-copy";
import { AuthMarketingPanel } from "./-components/auth-marketing-panel";

const signupSearchSchema = z.object({ email: z.string().optional() });

export const Route = createFileRoute("/auth/signup")({
  component: RouteComponent,
  validateSearch: zodValidator(signupSearchSchema),
  staticData: { title: "Create your workspace" },
});

const fieldClass =
  "h-auto rounded-lg border-[#d5d7da] bg-white px-3.5 py-2.5 text-base text-[#181d27] shadow-[0px_1px_2px_rgba(10,13,18,0.05)] placeholder:text-[#717680] focus-visible:border-[#dd2590] focus-visible:ring-[#dd2590]/25 aria-invalid:border-destructive aria-invalid:bg-destructive/5 aria-invalid:focus-visible:border-destructive aria-invalid:focus-visible:ring-destructive/20 dark:border-border dark:bg-input/30 dark:text-foreground";
const nameRule = /^\p{L}+(?:[ -]\p{L}+)*$/u;

const signupSchema = z
  .object({
    first_name: z
      .string()
      .trim()
      .min(2, "First name must be at least 2 characters")
      .max(40, "First name must be at most 40 characters")
      .regex(nameRule, "Use letters, spaces, or hyphens only"),
    last_name: z
      .string()
      .trim()
      .min(2, "Last name must be at least 2 characters")
      .max(40, "Last name must be at most 40 characters")
      .regex(nameRule, "Use letters, spaces, or hyphens only"),
    email: requiredEmail(
      "Enter a valid email address",
      "Enter your work email",
    ),
    company_name: z
      .string()
      .trim()
      .min(2, "Company name must be at least 2 characters")
      .max(120, "Company name must be at most 120 characters"),
    password: newPassword(),
    confirm_password: z.string().min(1, "Confirm your password"),
    terms_accepted: z.boolean(),
  })
  .superRefine((data, context) => {
    if (data.password !== data.confirm_password)
      context.addIssue({
        code: "custom",
        path: ["confirm_password"],
        message: "The passwords do not match",
      });
    if (!data.terms_accepted)
      context.addIssue({
        code: "custom",
        path: ["terms_accepted"],
        message: "The Terms & Conditions must be accepted",
      });
  });

type SignupValues = z.infer<typeof signupSchema>;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { email: emailFromQuery } = Route.useSearch();
  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: emailFromQuery ?? "",
      company_name: "",
      password: "",
      confirm_password: "",
      terms_accepted: false,
    },
  });
  const signup = useMutation({
    mutationFn: registerCompany,
    onSuccess(data) {
      navigate({
        to: "/auth/verify-code",
        search: {
          email: data.email,
          expires: data.expires_in_seconds,
          resend: data.resend_available_in_seconds,
        },
      });
    },
    onError(error: HTTPError) {
      const detail =
        error.data && typeof error.data === "object" && "detail" in error.data
          ? error.data.detail
          : null;
      if (typeof detail === "string")
        return form.setError("root.serverError", { message: detail });
      const fieldErrors = parseBackendError(detail);
      if (fieldErrors) {
        fieldErrors.forEach(([field, message]) =>
          form.setError(field as keyof SignupValues, { message }),
        );
        return;
      }
      const refusal = parseRefusal(detail);
      if (refusal?.code === "EMAIL_ALREADY_REGISTERED")
        return form.setError("email", {
          message: "This email is already registered. Log in instead.",
        });
      form.setError("root.serverError", {
        message:
          (refusal?.code === "COMPANY_ALREADY_REGISTERED"
            ? "This company is already registered. Contact your administrator."
            : refusal?.message) ??
          "We could not create your workspace. Please try again.",
      });
    },
  });
  return (
    <AuthPageShell
      title="Create your workspace"
      description="Set up your company workspace in a few minutes."
    >
      <Form {...form}>
        <form
          className="flex flex-col gap-5"
          onSubmit={form.handleSubmit((values) =>
            signup.mutate({
              ...values,
              first_name: values.first_name.trim().replace(/\s+/g, " "),
              last_name: values.last_name.trim().replace(/\s+/g, " "),
              email: values.email.trim().toLowerCase(),
              terms_version: TERMS_VERSION,
            }),
          )}
        >
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="first_name"
              label="First name"
              autoComplete="given-name"
            />
            <TextField
              control={form.control}
              name="last_name"
              label="Last name"
              autoComplete="family-name"
            />
          </div>
          <TextField
            control={form.control}
            name="email"
            label="Work email"
            type="email"
            autoComplete="email"
          />
          <TextField
            control={form.control}
            name="company_name"
            label="Company name"
            autoComplete="organization"
          />
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <PasswordField
              control={form.control}
              name="password"
              label="Password"
            />
            <PasswordField
              control={form.control}
              name="confirm_password"
              label="Confirm password"
            />
          </div>
          <FormField
            control={form.control}
            name="terms_accepted"
            render={({ field }) => (
              <FormItem className="space-y-0">
                <div className="flex items-start gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      aria-required="true"
                      className="mt-0.5 aria-invalid:border-destructive aria-invalid:bg-destructive/5"
                    />
                  </FormControl>
                  <FormLabel className="cursor-pointer text-sm leading-5 font-normal">
                    I accept the{" "}
                    <a
                      href="https://polygraf.ai/terms-and-conditions/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#c11574] hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Terms &amp; Conditions
                    </a>
                    <RequiredMark />
                  </FormLabel>
                </div>
                <FieldMessage />
              </FormItem>
            )}
          />
          {form.formState.errors.root?.serverError && (
            <p className="text-sm text-destructive">
              {form.formState.errors.root.serverError.message}
            </p>
          )}
          <Button
            type="submit"
            disabled={signup.isPending}
            className="h-auto w-full rounded-lg bg-[#dd2590] py-2.5 text-base text-white hover:bg-[#c11574]"
          >
            <SubmitterText
              submitting={signup.isPending}
              submittingChildren="Creating workspace..."
            >
              Create workspace
            </SubmitterText>
          </Button>
          <p className="text-center text-sm">
            Already have an account?{" "}
            <Link to="/auth/login" className="text-[#c11574] hover:underline">
              Log in
            </Link>
          </p>
        </form>
      </Form>
    </AuthPageShell>
  );
}

function TextField({
  control,
  name,
  label,
  isRequired = true,
  ...props
}: {
  control: ReturnType<typeof useForm<SignupValues>>["control"];
  name: "first_name" | "last_name" | "email" | "company_name";
  label: string;
  isRequired?: boolean;
} & React.ComponentProps<typeof Input>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="gap-1.5">
          <FormLabel>
            {label}
            {isRequired ? <RequiredMark /> : null}
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              {...props}
              aria-required={isRequired}
              className={fieldClass}
            />
          </FormControl>
          <FieldMessage />
        </FormItem>
      )}
    />
  );
}
function PasswordField({
  control,
  name,
  label,
}: {
  control: ReturnType<typeof useForm<SignupValues>>["control"];
  name: "password" | "confirm_password";
  label: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="gap-1.5">
          <FormLabel>
            {label}
            <RequiredMark />
          </FormLabel>
          <FormControl>
            <PasswordInput
              {...field}
              autoComplete="new-password"
              aria-required="true"
              className={fieldClass}
            />
          </FormControl>
          <FieldMessage />
        </FormItem>
      )}
    />
  );
}

function RequiredMark() {
  return (
    <span className="ml-0.5 text-destructive" aria-hidden="true">
      *
    </span>
  );
}

/** Keeps the two-column form aligned when only one field has an error. */
function FieldMessage() {
  return (
    <div className="min-h-5">
      <FormMessage />
    </div>
  );
}

function AuthPageShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full w-full justify-center overflow-y-auto bg-white font-[Manrope,system-ui,sans-serif] dark:bg-background">
      <div className="grid w-full max-w-[1440px] grid-cols-1 lg:min-h-0 lg:grid-cols-[minmax(0,560fr)_minmax(0,880fr)]">
        <div className="flex min-w-0 flex-col bg-white px-4 py-6 sm:px-6 lg:px-8 lg:pt-10 dark:bg-background">
          <div className="mx-auto mb-8 w-full max-w-[520px] lg:mx-0">
            <PolygrafLogo className="h-8 w-auto [&_path[data-slot=name]]:fill-[#181d27] dark:[&_path[data-slot=name]]:fill-white" />
          </div>
          <div className="mx-auto flex w-full max-w-[520px] flex-1 flex-col justify-center py-4">
            <AuthMarketingHeroCopy className="mb-8 lg:hidden" />
            <div className="mb-8">
              <h1 className="text-3xl font-semibold text-[#181d27] dark:text-foreground">
                {title}
              </h1>
              <p className="mt-2 text-[#535862] dark:text-muted-foreground">
                {description}
              </p>
            </div>
            {children}
          </div>
          <footer className="mx-auto w-full max-w-[520px] pt-8 text-sm text-[#535862] dark:text-muted-foreground">
            © Polygraf Meeting Guard 2026
          </footer>
        </div>
        <AuthMarketingPanel />
      </div>
    </div>
  );
}
